import { useTasks } from '@app/context/TaskContext';
import { downloadFile } from '@app/hooks/useDownload';
import { useQuestionaireRoute } from '@app/hooks/useQuestionaireRoute';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { useCreateQuestionnaireMutation } from '@dieterApi/questionnaire/useCreateQuestionnaireMutation';
import { useDeleteQuestionnaireMutation } from '@dieterApi/questionnaire/useDeleteQuestionnaireMutation';
import { useOpenQuestionnaireMutation } from '@dieterApi/questionnaire/useOpenQuestionnaireMutation';
import { useQuestionnaireDownloadDocxV2Mutation } from '@dieterApi/questionnaire/useQuestionnaireDownloadDocxV2Mutation';
import { useQuestionnaireDownloadPdfV2Mutation } from '@dieterApi/questionnaire/useQuestionnaireDownloadPdfV2Mutation';
import { useState } from 'react';

export type Dashboard = ReturnType<typeof useDashboard>;

export type Scope = [ScopeType, string];
export type ScopeType = 'create' | 'download' | 'open' | 'remove' | 'update';

export function useDashboard() {
  const { navigateToPath } = useQuestionaireRoute();

  const [create] = useCreateQuestionnaireMutation();
  const [open] = useOpenQuestionnaireMutation();
  const [remove] = useDeleteQuestionnaireMutation();

  // V2 mutations use local ID and render HTML locally (no dependency on legalOS DOCX)
  const [downloadDocxV2] = useQuestionnaireDownloadDocxV2Mutation();
  const [downloadPdfV2] = useQuestionnaireDownloadPdfV2Mutation();
  const { enqueueSnackbar } = useSnackbar();
  const tasks = useTasks();
  const { questionnaires } = useUserWithCompany();

  const [current, setCurrent] = useState<Scope | null>(null);

  function process(scope: Scope, callback: () => Promise<any>) {
    if (current) return;
    setCurrent(scope);
    callback()
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
        console.error(e);
      })
      .finally(() => setCurrent(null));
  }

  return {
    isBusy: current !== null,
    affects(type?: ScopeType, id?: string) {
      return !!(current && (!type || current[0] === type) && (!id || current[1] === id));
    },
    create(
      appId: string,
      section?: number,
      navigate: boolean = true,
      userValueIndex?: string,
      token?: string,
      urlToken?: string,
      name?: string, // name of external user working anonymously with token on the questionnaire (Datenschutz Grundschulung)
      onlyIfNotExists?: boolean
    ) {
      process(['create', userValueIndex || appId], async () => {
        const { data } = await create({ variables: { appId: appId, userValueIndex, token, onlyIfNotExists } });

        const questionnaireId = data?.createQuestionnaire.questionnaireId;
        if (data && navigate) {
          navigateToPath({ questionnaireId, section, token: urlToken, name });
        }
      });
    },
    download(localQuestionnaireId: string, fileName?: string, type: 'pdf' | 'docx' = 'docx') {
      process(['download', localQuestionnaireId], async function () {
        // First, validate/recreate questionnaire on legalOS if needed
        const { data } = await open({ variables: { localQuestionnaireId } });
        if (!data) return;

        // V2 mutations use local ID and render HTML locally
        switch (type) {
          case 'pdf':
            return downloadPdfV2({
              variables: {
                id: localQuestionnaireId,
                fileName,
              },
            }).then((result) => {
              const downloadData = result?.data?.questionnaireDownloadPdfV2;
              downloadData && downloadFile(downloadData);
            });

          case 'docx':
            return downloadDocxV2({
              variables: {
                id: localQuestionnaireId,
                fileName,
              },
            }).then((result) => {
              const downloadData = result?.data?.questionnaireDownloadDocxV2;
              downloadData && downloadFile(downloadData);
            });
          default:
            return;
        }
      });
    },
    open(localQuestionnaireId: string, section?: number, revisit: boolean = false, token?: string, urlToken?: string) {
      process(['open', localQuestionnaireId], async function () {
        const { data } = await open({ variables: { localQuestionnaireId, token } });
        if (data) {
          const questionnaireId = data?.openQuestionnaire.questionnaireId;
          navigateToPath({ questionnaireId, section, revisit, token: urlToken });
        }
      });
    },
    remove(localQuestionnaireId: string) {
      process(['remove', localQuestionnaireId], async () => {
        const thisDoc = questionnaires?.find((q) => q.id === localQuestionnaireId);
        const task = tasks?.find((task) => thisDoc?.application.id === task.applicationId);
        task?.setStatus(false);

        return remove({ variables: { localQuestionnaireId } });
      });
    },
  };
}
